import React, { ReactNode } from 'react'
import { Box, Button, CircularProgress, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { ButtonProps } from '@mui/material/Button'

import BaseModal from './BaseModal'

export type ActionModalVariant = 'primary' | 'error' | 'success' | 'warning'

const VARIANT_COLORS = {
  primary: {
    backgroundColor: '#DBE8FE',
    borderColor: '#DBE8FE'
  },
  error: {
    backgroundColor: '#FEF2F2',
    borderColor: '#FEE2E2'
  },
  success: {
    backgroundColor: '#E6F0D7',
    borderColor: '#C5D9A8'
  },
  warning: {
    backgroundColor: '#FFFBEB',
    borderColor: '#FDE68A'
  }
}

interface ActionIconProps {
  children: ReactNode
  variant?: ActionModalVariant
}

export const ActionIcon = ({ children, variant = 'primary' }: ActionIconProps) => {
  const theme = useTheme()
  const color = variant === 'error'
    ? theme.palette.error.main
    : variant === 'success'
      ? theme.palette.success.main
      : variant === 'warning'
        ? theme.palette.warning.main
        : theme.palette.primary.main

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: VARIANT_COLORS[variant].backgroundColor,
        border: `1px solid ${VARIANT_COLORS[variant].borderColor}`,
        borderRadius: '50%',
        color,
        display: 'flex',
        flexShrink: 0,
        height: 40,
        justifyContent: 'center',
        width: 40
      }}
    >
      {children}
    </Box>
  )
}

export const ActionTitle = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ color: 'content.primary', fontSize: 20, fontWeight: 600, textAlign: 'center' }}>
    {children}
  </Typography>
)

export interface ActionModalProps {
  cancelLabel: ReactNode
  children?: ReactNode
  closeLabel?: string
  confirmColor?: ButtonProps['color']
  confirmLabel: ReactNode
  description?: ReactNode
  disableClose?: boolean
  hideActions?: boolean
  icon?: ReactNode
  loading?: boolean
  onCancel?: () => void | Promise<unknown>
  onClose: () => void
  onConfirm: () => void | Promise<unknown>
  title: ReactNode
  variant?: ActionModalVariant
  width?: number | string
}

const ActionModal = ({
  cancelLabel,
  children,
  closeLabel = 'Fermer',
  confirmColor,
  confirmLabel,
  description,
  disableClose = false,
  hideActions = false,
  icon = <i className="fa-regular fa-circle-info" />,
  loading = false,
  onCancel,
  onClose,
  onConfirm,
  title,
  variant = 'error',
  width = 500
}: ActionModalProps) => {
  const canClose = !disableClose && !loading

  return (
    <BaseModal
      onClose={canClose ? onClose : undefined}
      paperSx={{
        borderRadius: '16px',
        maxWidth: 'calc(100vw - 32px)',
        width
      }}
    >
      <Stack alignItems="center" gap={2} padding={4} position="relative">
        {canClose && (
          <IconButton
            aria-label={closeLabel}
            onClick={onClose}
            size="small"
            sx={{
              borderRadius: '50%',
              cursor: 'pointer',
              height: 32,
              padding: 0,
              position: 'absolute',
              right: 12,
              top: 12,
              width: 32,
              '& i': {
                lineHeight: 1,
                pointerEvents: 'none'
              }
            }}
          >
            <i aria-hidden="true" className="fa-light fa-xmark" style={{ fontSize: 14 }} />
          </IconButton>
        )}

        <ActionIcon variant={variant}>{icon}</ActionIcon>
        <ActionTitle>{title}</ActionTitle>

        {description && (
          typeof description === 'string'
            ? (
              <Typography sx={{ color: 'content.secondary', fontSize: 14, textAlign: 'center' }}>
                {description}
              </Typography>
            )
            : description
        )}

        {children}

        {!hideActions && (
          <Stack direction="row" justifyContent="space-between" mt={1} width="100%">
            <Button disabled={loading} onClick={onCancel ?? onClose} size="small" variant="outlined">
              {cancelLabel}
            </Button>
            <Button
              color={confirmColor ?? (variant === 'warning' ? 'warning' : variant)}
              disabled={loading}
              onClick={onConfirm}
              size="small"
              variant="contained"
            >
              {loading && <CircularProgress color="inherit" size={12} sx={{ mr: 1 }} />}
              {confirmLabel}
            </Button>
          </Stack>
        )}
      </Stack>
    </BaseModal>
  )
}

export default ActionModal
